import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { useTheme } from "../lib/theme";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";
import { Flame, Gift, Moon, Sun, Twitch, LogOut, BadgeCheck, Chrome, Info, MessageCircle, ExternalLink, Gamepad2, RefreshCw, Unlink, Youtube } from "lucide-react";
import { BADGES, currentBadge, nextBadge, BadgeIcon, DonorBadge, SubBadge } from "../lib/badges";

function SteamCard() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const linked = !!user?.steam;

  const load = (force = false) => {
    api.get(`/steam/me${force ? "?refresh=1" : ""}`).then(({ data }) => setData(data)).catch(() => setData(null));
  };
  useEffect(() => {
    if (linked) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked]);

  const unlink = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete("/steam/link");
      toast.success(t("steam.toast_unlinked"));
      setData(null);
      refresh();
    } catch {
      toast.error(t("gv.toast_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 mb-8" data-testid="profile-steam-card">
      <div className="flex items-center gap-3 mb-3">
        <Gamepad2 className="w-5 h-5 text-[#66c0f4]" />
        <p className="hud-label">{t("steam.title")}</p>
      </div>
      {!linked ? (
        <>
          <p className="text-sm text-steel mb-6">{t("steam.text_off")}</p>
          <a
            href={`${API}/steam/link`}
            data-testid="steam-link-button"
            className="chamfer-sm bg-[#1b2838] hover:bg-[#2a475e] border border-[#66c0f4]/40 text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 inline-flex items-center gap-2 transition-colors duration-300"
          >
            <Gamepad2 className="w-4 h-4 text-[#66c0f4]" /> {t("steam.link")}
          </a>
        </>
      ) : (
        <div data-testid="steam-linked">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {(data?.player?.avatarfull || user.steam.avatarfull) && (
              <img src={data?.player?.avatarfull || user.steam.avatarfull} alt="" className="w-14 h-14 chamfer-sm border border-[#1A1A1A]" data-testid="steam-avatar" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-display text-2xl uppercase truncate" data-testid="steam-name">{data?.player?.personaname || user.steam.personaname || "Steam"}</p>
              {(data?.player?.profileurl || user.steam.profileurl) && (
                <a href={data?.player?.profileurl || user.steam.profileurl} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#66c0f4] hover:underline" data-testid="steam-profile-link">
                  {t("steam.view")} ↗
                </a>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => load(true)} disabled={busy} data-testid="steam-refresh-button" className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-wardogs font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 inline-flex items-center gap-2 transition-colors duration-300">
                <RefreshCw className="w-3.5 h-3.5" /> {t("steam.refresh")}
              </button>
              <button onClick={unlink} disabled={busy} data-testid="steam-unlink-button" className="chamfer-sm border border-[#2A2A2A] hover:border-valkyra text-steel hover:text-valkyra font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 inline-flex items-center gap-2 transition-colors duration-300">
                <Unlink className="w-3.5 h-3.5" /> {t("steam.unlink")}
              </button>
            </div>
          </div>

          {data?.private && <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-wardogs mb-4" data-testid="steam-private-note">{t("steam.private")}</p>}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A] mb-6">
            <div className="bg-[#050505] p-4 text-center">
              <p className="font-display text-3xl text-wardogs leading-none" data-testid="steam-hours">{data?.playtime_hours != null ? data.playtime_hours : "—"}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-steel mt-1">{t("steam.hours")}</p>
            </div>
            <div className="bg-[#050505] p-4 text-center">
              <p className="font-display text-3xl text-wardogs leading-none" data-testid="steam-achievements-count">
                {data?.achievements_unlocked != null ? `${data.achievements_unlocked}/${data.achievements_total}` : "—"}
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-steel mt-1">{t("steam.achievements")}</p>
            </div>
            <div className="bg-[#050505] p-4 text-center col-span-2 md:col-span-1">
              <p className="font-display text-3xl text-wardogs leading-none" data-testid="steam-owns">
                {data?.owns_wardogs == null ? "—" : data.owns_wardogs ? "✓" : "✗"}
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-steel mt-1">WARDOGS</p>
            </div>
          </div>
          {data?.owns_wardogs === false && <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel mb-4">{t("steam.not_owned")}</p>}

          {data?.achievements?.length > 0 && (
            <div className="mb-6">
              <p className="hud-label mb-3">{t("steam.achievements")}</p>
              <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="steam-achievements-list">
                {data.achievements.map((a, i) => (
                  <div key={i} className="bg-[#050505] px-4 py-2.5 flex items-center gap-3">
                    {a.icon && <img src={a.icon} alt="" className="w-8 h-8" loading="lazy" />}
                    <span className="font-mono text-[11px] text-white truncate">{a.name}</span>
                    {a.unlocktime > 0 && (
                      <span className="ml-auto font-mono text-[9px] text-[#4A4D51] shrink-0">
                        {new Date(a.unlocktime * 1000).toLocaleDateString("it-IT")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data?.stats?.length > 0 ? (
            <div>
              <p className="hud-label mb-3">{t("steam.stats")}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="steam-stats-grid">
                {data.stats.map((s, i) => (
                  <div key={i} className="bg-[#050505] p-3 text-center">
                    <p className="font-display text-2xl text-white leading-none">{s.value}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-steel mt-1 truncate">{s.name}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4A4D51]" data-testid="steam-no-stats">{t("steam.no_stats")}</p>
          )}
        </div>
      )}
    </div>
  );
}

const SOCIAL_FIELDS = [
  { key: "twitch", label: "Twitch", icon: Twitch, color: "#9146FF" },
  { key: "discord", label: "Discord", icon: MessageCircle, color: "#5865F2" },
  { key: "google", label: "Google", icon: Chrome, color: "#4285F4" },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000" },
];

function SocialsCard() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [configured, setConfigured] = useState({});
  const [busy, setBusy] = useState("");
  const socials = user?.socials || {};
  const provider = user?.provider || "twitch";
  const googleAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  useEffect(() => {
    api.get("/config").then(({ data }) => setConfigured(data.social_oauth || {})).catch(() => {});
  }, []);

  const unlink = async (key) => {
    if (busy) return;
    setBusy(key);
    try {
      if (key === "discord" && user?.discord_linked) {
        await api.delete("/discord/link");
        toast.success(t("profile.discord_toast_unlinked"));
      } else if (key === "twitch" || key === "google") {
        await api.delete(`/${key}/link`);
        toast.success(t("profile.socials_unlinked_done", { social: key === "twitch" ? "Twitch" : "Google" }));
      } else {
        await api.post("/me/socials", { ...socials, [key]: "" });
        toast.success(t("profile.socials_saved"));
      }
      refresh();
    } catch {
      toast.error(t("profile.discord_toast_error"));
    } finally {
      setBusy("");
    }
  };

  const rowState = (key) => {
    if (key === "twitch") {
      const primary = provider === "twitch";
      return { primary, linked: primary || !!user?.linked_twitch_id, name: primary ? user?.display_name : user?.linked_twitch_login, ready: true, href: `${API}/twitch/link` };
    }
    if (key === "google") {
      const primary = provider === "google";
      return { primary, linked: primary || !!user?.google_id, name: primary ? user?.display_name : (user?.google_email || socials.google), ready: true, href: googleAuthUrl };
    }
    if (key === "discord") {
      const primary = provider === "discord";
      return { primary, linked: primary || !!user?.discord_linked, name: primary ? user?.display_name : (user?.discord_username || socials.discord), ready: true, href: `${API}/discord/link` };
    }
    return { primary: false, linked: !!socials[key], name: socials[key], ready: !!configured[key], href: `${API}/social/${key}/link` };
  };

  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 mb-8" data-testid="profile-socials-card">
      <div className="flex items-center gap-3 mb-3">
        <ExternalLink className="w-5 h-5 text-wardogs" />
        <p className="hud-label">{t("profile.socials_title")}</p>
      </div>
      <p className="text-sm text-steel mb-6">{t("profile.socials_text")}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {SOCIAL_FIELDS.map((f) => {
          const st = rowState(f.key);
          return (
            <div key={f.key} className="flex items-center gap-3 chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3.5" data-testid={`social-row-${f.key}`}>
              <f.icon className="w-5 h-5 shrink-0" style={{ color: f.color }} />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">{f.label}</p>
                {st.linked && (
                  <p className="font-mono text-[10px] text-steel truncate" data-testid={`social-linked-${f.key}`}>
                    {t("profile.socials_linked_as", { name: st.name })}
                  </p>
                )}
              </div>
              {st.linked && st.primary ? (
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-manticore shrink-0" data-testid={`social-primary-${f.key}`}>
                  {t("profile.socials_primary")}
                </span>
              ) : st.linked ? (
                <button
                  onClick={() => unlink(f.key)}
                  disabled={busy === f.key}
                  data-testid={`social-unlink-${f.key}`}
                  className="chamfer-sm border border-[#2A2A2A] hover:border-valkyra text-steel hover:text-valkyra font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 transition-colors duration-300 disabled:opacity-50"
                >
                  {t("profile.socials_unlink")}
                </button>
              ) : st.ready ? (
                <a
                  href={st.href}
                  data-testid={`social-connect-${f.key}`}
                  className="chamfer-sm font-mono text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2.5 text-white hover:brightness-110 transition-all duration-300"
                  style={{ background: f.color }}
                >
                  {t("profile.socials_connect")}
                </a>
              ) : (
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#4A4D51]" data-testid={`social-soon-${f.key}`}>
                  {t("profile.socials_soon")}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Profilo() {
  const { user, login, loginGoogle, loginDiscord, logout, refresh } = useAuth();
  const { t } = useLang();
  const { theme, toggle } = useTheme();
  const [params] = useSearchParams();
  const [rechecking, setRechecking] = useState(false);

  // Ricontrollo live dell'abbonamento (badge "Abbonato" verificato)
  const recheckSub = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/profile/recheck-sub");
      if (data.subscribed) {
        toast.success(t("profile.recheck_ok"));
        await refresh();
      } else if (data.reason === "token_expired" || data.reason === "no_token") {
        toast.error(t("profile.recheck_relogin"));
      } else {
        toast.error(t("profile.recheck_ko"));
      }
    } catch {
      toast.error(t("profile.recheck_ko"));
    } finally {
      setRechecking(false);
    }
  };

  useEffect(() => {
    if (params.get("auth") === "ok") {
      refresh();
      toast.success(t("com.auth_ok"));
    }
    const steamParam = params.get("steam");
    if (steamParam === "linked") {
      refresh();
      toast.success(t("steam.toast_linked"));
    } else if (steamParam === "clash") {
      toast.error(t("steam.toast_clash"));
    } else if (steamParam === "error") {
      toast.error(t("steam.toast_error"));
    }
    const discordParam = params.get("discord");
    if (discordParam === "linked") {
      refresh();
      toast.success(t("profile.discord_toast_linked"));
    } else if (discordParam === "clash") {
      toast.error(t("profile.discord_toast_clash"));
    } else if (discordParam === "own_account") {
      toast.error(t("profile.discord_toast_own"));
    } else if (discordParam === "error") {
      toast.error(t("profile.discord_toast_error"));
    }
    const socialParam = params.get("social");
    if (socialParam?.startsWith("linked_")) {
      refresh();
      toast.success(t("profile.socials_linked", { social: socialParam.replace("linked_", "") }));
    } else if (socialParam?.startsWith("clash_")) {
      toast.error(t("profile.socials_clash", { social: socialParam.replace("clash_", "") }));
    } else if (socialParam === "own_twitch") {
      toast.error(t("profile.socials_own_twitch"));
    } else if (socialParam?.startsWith("error_")) {
      toast.error(t("profile.socials_error", { social: socialParam.replace("error_", "") }));
    }
  }, [params, refresh, t]);

  const tiktokError = params.get("social") === "error_tiktok";

  const roleColor = { lonestar: "#00A3FF", valkyra: "#FF3B30", manticore: "#34C759" }[user?.role] || "#8A8D91";
  const streak = user?.streak || 0;
  const visits = user?.visits || 0;
  const badge = currentBadge(visits);
  const next = nextBadge(visits);

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("profile.over")}</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-12" data-testid="profile-title">
            {t("profile.title")}
          </h1>
        </Reveal>

        {tiktokError && (
          <Reveal>
            <div className="chamfer border border-[#FE2C55]/50 bg-[#140a0c] p-6 mb-8" data-testid="tiktok-error-banner">
              <p className="font-display text-xl uppercase text-[#FE2C55] mb-2">{t("profile.tiktok_help_title")}</p>
              <p className="text-sm text-steel leading-relaxed">{t("profile.tiktok_help_text")}</p>
            </div>
          </Reveal>
        )}

        {!user ? (
          <Reveal>
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10" data-testid="profile-login-gate">              <h2 className="font-display text-3xl uppercase mb-2">{t("com.gate_title")}</h2>
              <p className="text-steel text-sm max-w-lg mb-8">{t("com.gate_text")}</p>
              <div className="flex flex-wrap gap-4">
                <button onClick={login} data-testid="profile-twitch-login" className="chamfer-sm bg-[#9146FF] hover:bg-[#7d3aed] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300 flex items-center gap-2">
                  <Twitch className="w-4 h-4" /> {t("nav.login_twitch")}
                </button>
                <button onClick={loginGoogle} data-testid="profile-google-login" className="chamfer-sm border border-[#2A2A2A] hover:border-white text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300 flex items-center gap-2">
                  <Chrome className="w-4 h-4" /> Google
                </button>
                <button onClick={loginDiscord} data-testid="profile-discord-login" className="chamfer-sm bg-[#5865F2] hover:bg-[#4752c4] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Discord
                </button>
              </div>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 md:p-10 mb-8 relative overflow-hidden" data-testid="profile-card">
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: roleColor }} />
                <div className="flex flex-col md:flex-row md:items-center gap-8">
                  {user.avatar ? (
                    <img src={user.avatar} alt="" className="w-24 h-24 chamfer border border-[#1A1A1A]" />
                  ) : (
                    <span className="w-24 h-24 chamfer bg-[#1A1A1A]" />
                  )}
                  <div className="flex-1">
                    <p className="font-display text-4xl uppercase leading-none mb-2" data-testid="profile-name">{user.display_name}</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel mb-4">
                      {t("profile.via", { provider: user.provider === "google" ? "Google" : user.provider === "discord" ? "Discord" : "Twitch" })}
                      {user.role && <> — <span style={{ color: roleColor }}>{user.role}</span></>}
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="flex items-center gap-2 font-mono text-xs text-steel" data-testid="profile-streak">
                        <Flame className={`w-4 h-4 ${streak >= 3 ? "text-wardogs" : "text-[#2A2A2A]"}`} />
                        {t("profile.streak")}: {streak}
                      </span>
                      <span className="flex items-center gap-2 font-mono text-xs text-steel" data-testid="profile-visits">
                        <BadgeIcon visits={visits} testid="profile-badge-icon" />
                        {t("profile.visits")}: {visits}
                      </span>
                      {badge && (
                        <span
                          className="chamfer-sm border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"
                          style={{ borderColor: `${badge.color}66`, color: badge.color, background: `${badge.color}14`, boxShadow: `0 0 12px ${badge.color}33` }}
                          data-testid="profile-loyalty-badge"
                        >
                          <BadgeCheck className="w-3.5 h-3.5" /> {t("profile.streak_badge", { n: badge.at })}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={logout} data-testid="profile-logout-button" className="chamfer-sm border border-[#2A2A2A] hover:border-valkyra text-steel hover:text-valkyra font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3 transition-colors duration-300 flex items-center gap-2 self-start">
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </button>
                </div>
              </div>
            </Reveal>

            <div className="grid gap-8 md:grid-cols-2 mb-8">
              <Reveal delay={0.05}>
                <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 h-full" data-testid="profile-theme-card">
                  <p className="hud-label mb-5">{t("profile.theme")}</p>
                  <div className="flex gap-px border border-[#1A1A1A]">
                    <button onClick={() => theme !== "dark" && toggle()} data-testid="theme-dark-button" className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${theme === "dark" ? "bg-wardogs text-[#050505] font-bold" : "bg-[#050505] text-steel hover:text-white"}`}>
                      <Moon className="w-4 h-4" /> {t("profile.theme_dark")}
                    </button>
                    <button onClick={() => theme !== "light" && toggle()} data-testid="theme-light-button" className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${theme === "light" ? "bg-wardogs text-[#050505] font-bold" : "bg-[#050505] text-steel hover:text-white"}`}>
                      <Sun className="w-4 h-4" /> {t("profile.theme_light")}
                    </button>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 h-full" data-testid="profile-account-card">
                  <p className="hud-label mb-5">{t("profile.account")}</p>
                  <div className="flex items-center gap-3 mb-4">
                    {user.provider === "google" ? <Chrome className="w-5 h-5 text-white" /> : user.provider === "discord" ? <MessageCircle className="w-5 h-5 text-[#5865F2]" /> : <Twitch className="w-5 h-5 text-[#9146FF]" />}
                    <span className="font-mono text-xs text-white">{user.provider === "google" ? "Google" : user.provider === "discord" ? "Discord" : "Twitch"}</span>
                    {user.subscribed && <SubBadge tier={user.sub_tier} variant="full" testid="profile-sub-badge" />}
                  </div>
                  <p className="text-xs text-steel leading-relaxed flex items-start gap-2 mb-4">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {t("profile.no_password")}
                  </p>
                  <button
                    onClick={recheckSub}
                    disabled={rechecking}
                    data-testid="profile-recheck-sub"
                    className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel hover:text-wardogs transition-colors duration-300 inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${rechecking ? "animate-spin" : ""}`} />
                    {rechecking ? t("profile.rechecking") : t("profile.recheck")}
                  </button>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.12}>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 mb-8" data-testid="profile-badges-card">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                  <div>
                    <p className="hud-label mb-2">{t("profile.badges_title")}</p>
                    <p className="text-sm text-steel" data-testid="profile-badge-progress">
                      {next ? t("profile.badge_next", { n: next.at }) : t("profile.badge_max")} — {t("profile.visits")}: {visits}
                    </p>
                    {user?.donated && (
                      <span
                        className="chamfer-sm border border-[#FFD700]/60 bg-[#FFD700]/10 px-3 py-1.5 mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFD700] inline-flex items-center gap-2"
                        style={{ boxShadow: "0 0 14px #FFD70033" }}
                        data-testid="profile-donor-badge"
                      >
                        <DonorBadge /> {t("badge.donor")}
                      </span>
                    )}
                  </div>
                  {badge && (
                    <div className="text-right">
                      <p className="font-display text-2xl uppercase leading-none" style={{ color: badge.color, textShadow: `0 0 14px ${badge.color}66` }} data-testid="profile-badge-rank">
                        {t(`badge.${badge.key}`)}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel mt-1">{t("badge.day", { n: Math.min(visits, 100) })}</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 md:grid-cols-11 gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
                  {BADGES.map((b) => {
                    const unlocked = visits >= b.at;
                    const Icon = b.icon;
                    return (
                      <div
                        key={b.key}
                        data-testid={`profile-badge-${b.at}`}
                        className="bg-[#050505] p-3 flex flex-col items-center gap-1.5 transition-all duration-500"
                        style={unlocked ? { boxShadow: `inset 0 0 18px ${b.color}22` } : {}}
                        title={`${t(`badge.${b.key}`)} — ${b.at} ${t("profile.visits").toLowerCase()}`}
                      >
                        <Icon
                          className={`w-5 h-5 transition-all duration-500 ${unlocked ? b.fx : ""}`}
                          style={{ color: unlocked ? b.color : "#2A2A2A", filter: unlocked ? `drop-shadow(0 0 6px ${b.color}88)` : "none" }}
                        />
                        <span className="font-mono text-[9px]" style={{ color: unlocked ? b.color : "#4A4D51" }}>{b.at}</span>
                        <span className="font-mono text-[8px] uppercase tracking-wide text-center leading-tight" style={{ color: unlocked ? b.color : "#4A4D51" }}>{t(`badge.${b.key}`)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.13}>
              <SteamCard />
            </Reveal>

            <Reveal delay={0.14}>
              <SocialsCard />
            </Reveal>

            <Reveal delay={0.15}>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8" data-testid="profile-drops-card">
                <div className="flex items-center gap-3 mb-3">
                  <Gift className="w-5 h-5 text-[#9146FF]" />
                  <p className="hud-label">{t("profile.drops")}</p>
                </div>
                <p className="text-sm text-steel mb-6">{t("profile.drops_text")}</p>
                <a
                  href="https://account.wardogs.com/#/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="profile-drops-link"
                  className="chamfer-sm bg-[#9146FF] hover:bg-[#7d3aed] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 inline-flex items-center gap-2 transition-colors duration-300"
                >
                  <ExternalLink className="w-4 h-4" /> {t("profile.drops_cta")}
                </a>
              </div>
            </Reveal>
          </>
        )}
      </div>
    </main>
  );
}
